import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Resource } from 'types';
import { motion, AnimatePresence } from 'framer-motion';
import { Button, Typography, Tag, COLORS } from 'pvs-design-system';
import { Box, Stack, Divider } from '@mui/material';
import { getItemTypeLabel } from '../../utils/fileUtils';
import { useResourceStatisticsQuery } from 'api/useLibraryApi';
import ContentCopy from '@mui/icons-material/ContentCopy';
import Check from '@mui/icons-material/Check';
import OpenInNew from '@mui/icons-material/OpenInNew';

interface DetailsPanelProps {
  item: Resource | null;
  onClose: () => void;
  /** Whether the current user may view resource statistics. The statistics
   *  endpoint requires manage permission (it returns 403 otherwise), so callers
   *  pass the user's manage permission here to both skip the request and hide
   *  the statistics section for users without it. */
  canViewStatistics?: boolean;
}

const PANEL_WIDTH = 340;

/** Placeholder shown for statistics the backend leaves empty (e.g. a resource
 *  that has never been modified). An em dash is the common convention for
 *  "no value" in detail/stat views. */
const STAT_EMPTY = '—';

/** Formats a "{date} by {author}" statistic: omits the trailing "by …" when the
 *  author is missing and falls back to the placeholder when there is no date at
 *  all, so it never renders a dangling separator. */
const formatDateBy = (date?: string | null, by?: string | null): string => {
  const d = date?.trim();
  if (!d) return STAT_EMPTY;
  const author = by?.trim();
  return author ? `${d} by ${author}` : d;
};

/** Formats a single-value statistic, falling back to the placeholder when empty. */
const formatValue = (value?: string | null): string => value?.trim() || STAT_EMPTY;

/** One "Opens" metric: a large count above a short period label. A zero reads in
 *  the muted colour so an unused period never competes with a real number. */
const OpenStat: React.FC<{ value: number; label: string }> = ({ value, label }) => (
  <Box
    sx={{
      flex: 1,
      minWidth: 0,
      textAlign: 'center',
      py: 1.25,
      px: 0.5,
      bgcolor: COLORS.beige[200],
      borderRadius: 1.5,
    }}
  >
    <Typography
      variant="h4"
      component="div"
      style={{
        margin: 0,
        fontWeight: 700,
        lineHeight: 1.1,
        whiteSpace: 'nowrap',
        color: value > 0 ? COLORS.gray[900] : COLORS.gray[400],
      }}
    >
      {value.toLocaleString()}
    </Typography>
    <Typography variant="p-xs" component="div" style={{ color: COLORS.gray[400], marginTop: 4 }}>
      {label}
    </Typography>
  </Box>
);

/** One "Details" line: a plain-language label followed by its value (e.g.
 *  "Added 7/31/2014 by Jennifer Volpe"). Missing values render in the muted colour
 *  so they read as "no data" rather than a broken field. */
const DetailLine: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <Typography
    variant="p-sm"
    style={{ lineHeight: '20px', color: value === STAT_EMPTY ? COLORS.gray[400] : COLORS.gray[900] }}
  >
    {label} {value}
  </Typography>
);

/** Shimmer fill shared by the statistics skeleton bars/boxes. */
const SHIMMER_SX = {
  borderRadius: 0.5,
  background: 'linear-gradient(90deg, #f5f5f5 25%, #fafafa 50%, #f5f5f5 75%)',
  backgroundSize: '200% 100%',
  animation: 'shimmer 1.4s infinite',
} as const;

/** Loading placeholder shaped like the real statistics content — an "Opens" row of
 *  three boxes and three "Details" lines (each with a small heading) — so the panel
 *  doesn't reflow when the data lands. */
const StatsSkeleton: React.FC = () => (
  <>
    {/* Opens: heading + 3 stat boxes */}
    <Box sx={{ ...SHIMMER_SX, height: 10, width: 44, mb: 1 }} />
    <Stack direction="row" gap={1} sx={{ mb: 2.5 }}>
      {[0, 1, 2].map((i) => (
        <Box key={i} sx={{ ...SHIMMER_SX, flex: 1, height: 58, borderRadius: 1.5 }} />
      ))}
    </Stack>
    {/* Details: heading + 3 lines */}
    <Box sx={{ ...SHIMMER_SX, height: 10, width: 44, mb: 1 }} />
    <Stack gap={0.75}>
      {[88, 82, 55].map((w, i) => (
        <Box key={i} sx={{ ...SHIMMER_SX, height: 12, width: `${w}%` }} />
      ))}
    </Stack>
  </>
);

const DetailsPanel: React.FC<DetailsPanelProps> = ({ item, onClose, canViewStatistics = false }) => {
  const [copied, setCopied] = useState(false);
  // Statistics require manage permission — the endpoint 403s for other users.
  // Skip the request entirely unless the caller says the user may view stats.
  const { data: stats, isFetching: isStatsFetching, isError: isStatsError } = useResourceStatisticsQuery(
    item?.id,
    { skip: !item || !canViewStatistics },
  );

  const handleCopyLink = () => {
    if (!item) return;
    navigator.clipboard.writeText(item.url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const drawerRef = useRef<HTMLDivElement>(null);
  const openerRef = useRef<HTMLElement | null>(null);

  // Close on Escape — but only when the event hasn't already been handled and
  // focus is inside the drawer, so a dialog layered above it (TourModal, the
  // admin modals) can own Escape without also dismissing the drawer underneath.
  useEffect(() => {
    if (!item) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      if (drawerRef.current?.contains(document.activeElement)) {
        e.preventDefault();
        onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [item, onClose]);

  // Move focus into the drawer when it opens (so keyboard users land in it) and
  // restore focus to whatever opened it (e.g. the row's info button) on close.
  useEffect(() => {
    if (item) {
      // Capture the opener only on the closed → open transition, so switching
      // between resources while open keeps the original opener to return to.
      if (!openerRef.current) {
        openerRef.current = document.activeElement as HTMLElement | null;
      }
      drawerRef.current?.focus();
    } else if (openerRef.current) {
      openerRef.current.focus();
      openerRef.current = null;
    }
  }, [item]);

  // Rendered in a PORTAL on document.body so the drawer positions against the real
  // browser viewport (position: fixed) rather than the embedded widget, and can't be
  // clipped by any host/ancestor overflow. This is what keeps it pinned when the SPA
  // is embedded in a scrolling host (ASPX) page. Slides over from the right; on narrow
  // screens it becomes full width (acts like a modal).
  return createPortal(
    <AnimatePresence>
      {item && (
        <motion.div
          ref={drawerRef}
          key="details-drawer"
          tabIndex={-1}
          role="dialog"
          aria-label="Resource details"
          initial={{ x: '100%' }}
          animate={{ x: 0 }}
          exit={{ x: '100%' }}
          transition={{ duration: 0.24, ease: 'easeInOut' }}
          style={{
            position: 'fixed',
            top: 0,
            right: 0,
            height: '100vh',
            width: `min(100vw, ${PANEL_WIDTH}px)`,
            zIndex: 1200,
            display: 'flex',
            flexDirection: 'column',
            background: COLORS.white,
            borderLeft: `1px solid ${COLORS.gray[200]}`,
            boxShadow: '-8px 0 24px rgba(0,0,0,0.12)',
            overflow: 'hidden',
            outline: 'none',
          }}
        >
            {/* Header */}
            <Stack
              direction="row"
              alignItems="flex-start"
              justifyContent="space-between"
              sx={{ px: 2.5, pt: 2.5, pb: 2, borderBottom: `1px solid ${COLORS.gray[100]}` }}
            >
              <Box sx={{ flex: 1, pr: 1 }}>
                <Typography
                  variant="label-sm"
                  style={{
                    display: 'block',
                    color: COLORS.gray[400],
                    marginBottom: 6,
                  }}
                >
                  Resource details
                </Typography>
                <Typography
                  variant="p-md-semiBold"
                  component="h2"
                  style={{ margin: 0, color: COLORS.gray[900], lineHeight: '22px' }}
                >
                  {item.title}
                </Typography>
                <Box sx={{ mt: 1 }}>
                  <Tag variant="default" label={getItemTypeLabel(item.type)} size="small" />
                </Box>
              </Box>
              <Box
                component="button"
                onClick={onClose}
                aria-label="Close details panel"
                sx={{
                  flexShrink: 0, mt: 0.25,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  width: 28, height: 28,
                  border: 'none', background: 'none', cursor: 'pointer',
                  color: COLORS.gray[400], borderRadius: '50%', padding: 0,
                  '&:hover': { color: COLORS.gray[900], bgcolor: COLORS.gray[100] },
                }}
              >
                <Box component="span" aria-hidden="true" sx={{ fontSize: 20, lineHeight: 1 }}>×</Box>
              </Box>
            </Stack>

            {/* Action buttons */}
            <Stack
              direction="row"
              alignItems="center"
              gap={1}
              sx={{ px: 2, py: 1.25, borderBottom: `1px solid ${COLORS.gray[100]}`, flexShrink: 0 }}
            >
              <Button
                variant="text"
                size="small"
                onClick={handleCopyLink}
                icon={copied ? <Check style={{ fontSize: 15 }} /> : <ContentCopy style={{ fontSize: 15 }} />}
                sx={{
                  textTransform: 'none',
                  boxShadow: 'none',
                  color: copied ? '#2e7d32' : COLORS.gray[700],
                  '&:hover': { bgcolor: COLORS.gray[100] },
                }}
              >
                {copied ? 'Copied!' : 'Copy link'}
              </Button>
              <Button
                variant="contained"
                size="small"
                onClick={() => window.open(item.url, '_blank', 'noopener,noreferrer')}
                endIcon={<OpenInNew style={{ fontSize: 15 }} />}
                sx={{
                  flex: 1,
                  background: COLORS.purple[700],
                  '&:hover': { background: COLORS.purple[800] },
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: 2,
                  boxShadow: 'none',
                }}
              >
                Open resource
              </Button>
            </Stack>

            {/* Scrollable body */}
            <Box sx={{ flex: 1, overflowY: 'auto', px: 2.5, pt: 2, pb: 2 }}>
              {item.description && (
                <Box sx={{ mb: 2.5 }}>
                  <Typography
                    variant="label-sm"
                    component="h3"
                    style={{ display: 'block', color: COLORS.gray[400], marginBottom: 6 }}
                  >
                    Description
                  </Typography>
                  <Typography variant="p-sm" style={{ color: COLORS.gray[900], lineHeight: '20px' }}>
                    {item.description}
                  </Typography>
                </Box>
              )}

              {item.tags && item.tags.length > 0 && (
                <>
                  <Divider sx={{ borderColor: COLORS.gray[100], mb: 2 }} />
                  <Box sx={{ mb: 2.5 }}>
                    <Typography
                      variant="label-sm"
                      component="h3"
                      style={{ display: 'block', color: COLORS.gray[400], marginBottom: 8 }}
                    >
                      Tags
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {item.tags.map((tag) => (
                        <Tag
                          key={tag}
                          variant="default"
                          label={tag}
                          size="small"
                          sx={{ bgcolor: COLORS.gray[100], color: COLORS.gray[900] }}
                        />
                      ))}
                    </Box>
                  </Box>
                </>
              )}

              {canViewStatistics && (
              <>
                <Divider sx={{ borderColor: COLORS.gray[100], mb: 2 }} />

                {isStatsFetching && (
                  <Box sx={{ mb: 2.5 }}>
                    <StatsSkeleton />
                  </Box>
                )}

                {isStatsError && !isStatsFetching && (
                  <Box sx={{ mb: 2.5 }}>
                    <Typography variant="p-sm" style={{ color: COLORS.gray[400] }}>
                      Statistics unavailable
                    </Typography>
                  </Box>
                )}

                {stats && !isStatsError && !isStatsFetching && (
                  <>
                    {/* Opens — the three per-period counts grouped so "opens" is said
                        once instead of repeating on every row. */}
                    <Box sx={{ mb: 2.5 }}>
                      <Typography
                        variant="label-sm"
                        component="h3"
                        style={{ display: 'block', color: COLORS.gray[400], marginBottom: 8 }}
                      >
                        Opens
                      </Typography>
                      <Stack direction="row" gap={1}>
                        <OpenStat value={stats.hitsSinceCreation ?? 0} label="All time" />
                        <OpenStat value={stats.hitsLast6Months ?? 0} label="6 months" />
                        <OpenStat value={stats.hitsLast30Days ?? 0} label="30 days" />
                      </Stack>
                    </Box>

                    {/* Details */}
                    <Box sx={{ mb: 2.5 }}>
                      <Typography
                        variant="label-sm"
                        component="h3"
                        style={{ display: 'block', color: COLORS.gray[400], marginBottom: 8 }}
                      >
                        Details
                      </Typography>
                      <Stack gap={0.5}>
                        <DetailLine label="Added" value={formatDateBy(stats.created, stats.createdBy)} />
                        <DetailLine label="Updated" value={formatDateBy(stats.lastModified, stats.lastModifiedBy)} />
                        <DetailLine label="Last opened" value={formatValue(stats.lastAccessed)} />
                      </Stack>
                    </Box>
                  </>
                )}
              </>
              )}

              {/* URL — below statistics */}
              <Box sx={{ p: 1.5, bgcolor: COLORS.beige[200], borderRadius: 1.5, border: `1px solid ${COLORS.gray[200]}` }}>
                <Typography variant="label-sm" style={{ display: 'block', color: COLORS.gray[400], marginBottom: 4 }}>
                  Resource URL
                </Typography>
                <Typography
                  variant="p-sm"
                  style={{ color: COLORS.purple[700], wordBreak: 'break-all', lineHeight: '18px' }}
                >
                  {item.url}
                </Typography>
              </Box>
            </Box>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
};

export default DetailsPanel;
